var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { useCallback, useEffect, useState } from 'react';
import Knock from '@knocklabs/client';
import { getKnockSession } from 'actions/ServerActionCreators';
import { selectUnreadItems, selectUnseenItems } from 'utils/KnockFeedUtils';
/**
 * Manages the Virtual Coach Knock in-app feed lifecycle for the authenticated coach
 * (SCO-371): fetch a signed session → init the client + feed → subscribe to the
 * realtime store. Degrades gracefully at every step — if the session endpoint is
 * unconfigured (500 → null) or init fails, it simply yields an empty feed and
 * never throws, so the surface is dark-safe until Knock enhanced security is on.
 */
var useCoachKnockFeed = function (userId) {
    var _a = useState([]), items = _a[0], setItems = _a[1];
    var _b = useState(null), feed = _b[0], setFeed = _b[1];
    useEffect(function () {
        // Clear any prior user's feed immediately on re-run (user change / sign-out).
        setItems([]);
        setFeed(null);
        if (!userId) {
            return undefined;
        }
        var cancelled = false;
        var activeFeed;
        var activeKnock;
        var unsubscribe;
        var init = function () { return __awaiter(void 0, void 0, void 0, function () {
            var session, sync, error_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, getKnockSession()];
                    case 1:
                        session = _a.sent();
                        if (cancelled || !session) {
                            return [2 /*return*/];
                        }
                        _a.label = 2;
                    case 2:
                        _a.trys.push([2, 4, , 5]);
                        activeKnock = new Knock(session.knockPublicKey);
                        activeKnock.authenticate(String(userId), session.token);
                        activeFeed = activeKnock.feeds.initialize(session.feedChannelId);
                        sync = function () {
                            if (activeFeed) {
                                setItems(activeFeed.getState().items);
                            }
                        };
                        unsubscribe = activeFeed.store.subscribe(sync);
                        return [4 /*yield*/, activeFeed.fetch()];
                    case 3:
                        _a.sent();
                        // Bail if cleanup fired during the fetch — the cleanup owns teardown.
                        if (cancelled) {
                            return [2 /*return*/];
                        }
                        activeFeed.listenForUpdates();
                        sync();
                        setFeed(activeFeed);
                        return [3 /*break*/, 5];
                    case 4:
                        error_1 = _a.sent();
                        console.warn('Knock feed init failed; in-app feed disabled', error_1);
                        return [3 /*break*/, 5];
                    case 5: return [2 /*return*/];
                }
            });
        }); };
        init();
        return function () {
            cancelled = true;
            unsubscribe === null || unsubscribe === void 0 ? void 0 : unsubscribe();
            // Tear down BOTH the feed (broadcast channel) and the client (websocket +
            // page-visibility handlers) so remounts don't accumulate open sockets.
            activeFeed === null || activeFeed === void 0 ? void 0 : activeFeed.teardown();
            activeKnock === null || activeKnock === void 0 ? void 0 : activeKnock.teardown();
        };
    }, [userId]);
    var markInteracted = useCallback(function (item, metadata) { return __awaiter(void 0, void 0, void 0, function () {
        var error_2;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!feed) {
                        return [2 /*return*/];
                    }
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, feed.markAsInteracted(item, metadata)];
                case 2:
                    _a.sent();
                    return [3 /*break*/, 4];
                case 3:
                    error_2 = _a.sent();
                    console.warn('Failed to mark feed item interacted', error_2);
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/];
            }
        });
    }); }, [feed]);
    /*
     * Knock stamps `seen_at` on the store items synchronously, BEFORE the
     * network call (optimistic update, never rolled back), so the store echo
     * that follows a report already carries the stamp — filtering on it is the
     * whole dedupe. Consequence worth knowing: a failed write leaves the local
     * stamp in place, so it is not retried until a full refetch restores the
     * server's `seen_at: null`; the SCO-705 telemetry pair is where that shows.
     */
    var markSeen = useCallback(function (candidates) { return __awaiter(void 0, void 0, void 0, function () {
        var unseen, error_3;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!feed) {
                        return [2 /*return*/];
                    }
                    unseen = selectUnseenItems(candidates);
                    if (unseen.length === 0) {
                        return [2 /*return*/];
                    }
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, feed.markAsSeen(unseen)];
                case 2:
                    _a.sent();
                    return [3 /*break*/, 4];
                case 3:
                    error_3 = _a.sent();
                    console.warn('Failed to mark feed items seen', error_3);
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/];
            }
        });
    }); }, [feed]);
    var markRead = useCallback(function (candidates) { return __awaiter(void 0, void 0, void 0, function () {
        var unread, error_4;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!feed) {
                        return [2 /*return*/];
                    }
                    unread = selectUnreadItems(candidates);
                    if (unread.length === 0) {
                        return [2 /*return*/];
                    }
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, feed.markAsRead(unread)];
                case 2:
                    _a.sent();
                    return [3 /*break*/, 4];
                case 3:
                    error_4 = _a.sent();
                    console.warn('Failed to mark feed items read', error_4);
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/];
            }
        });
    }); }, [feed]);
    return {
        items: items,
        markInteracted: markInteracted,
        markSeen: markSeen,
        markRead: markRead
    };
};
export default useCoachKnockFeed;
